import { nextTick, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'

// How long the ring stays before fading — long enough to register as
// deliberate, short enough not to linger as visual noise.
const HIGHLIGHT_MS = 2000

// Deep-link target from AgentView's session context line (#1362) — scrolls
// to and briefly rings the repo card a session belongs to, so an admin
// jumping over from a session doesn't land at a cold page top and have to
// scan a flat, unordered list to find the right one. `repoExists` decides
// whether `?repo=` names something actually on the page, so a stale or
// mistyped link is a silent no-op rather than ringing nothing/erroring.
export function useRepoCardHighlight(repoExists: (slug: string) => boolean) {
  const route = useRoute()
  const highlightedSlug = ref<string | null>(null)
  let fadeTimer: ReturnType<typeof window.setTimeout> | null = null

  async function highlightFromQuery(): Promise<void> {
    const target = route.query.repo
    if (typeof target !== 'string' || !repoExists(target)) return

    highlightedSlug.value = target
    await nextTick()
    document
      .querySelector(`[data-testid="agent-repo-${target}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    fadeTimer = window.setTimeout(() => {
      highlightedSlug.value = null
    }, HIGHLIGHT_MS)
  }

  onUnmounted(() => {
    if (fadeTimer !== null) window.clearTimeout(fadeTimer)
  })

  return { highlightedSlug, highlightFromQuery }
}
